// Logic for the pastoral formation dashboard - see doc/pastoral-formation-concept.org.
//
// Everything here is pure and the clock is always a parameter (`today`, a Date)
// - exactly like lib/contract.js, lib/focus.js and lib/team.js - so all of it
// unit-tests without rendering and without freezing timers.
//
// Like the team record and unlike Contract/Week/Focus, this is not daily-cadence
// data: an encounter, an observation or a formation conversation lands on
// whatever day it lands on. So this module shapes exactly one state object -
// `{ dimensions, encounters, observed, practised, conversations }` - which lives
// in one persistent file (lib/formation_org.js).
//
// What is deliberately absent (concept §3) is most of what a formation dashboard
// would obviously have: there is no score, no total, no percentage and no streak
// anywhere in this module, and adding one would contradict the three findings the
// whole design rests on. Physicians' self-assessment tracks observed competence
// poorly and worst in the most confident (Davis et al. 2006), so a self-rating
// would measure the rating; role-model status is conferred by observers, not
// claimed (Cruess et al. 2008), so nothing here may compute exemplarity; and
// deliberate practice explains under 1% of performance variance in professions
// (Macnamara et al. 2014), so a practice counter would promise something the
// evidence does not support.
//
// The only things computed are facts about a calendar: what was last touched
// when, and whether a commitment made to a mentor was kept.

// The same local-date key the other dashboards file their days under - shared
// rather than re-derived, since getting it wrong (toISOString(), which formats
// in UTC) is the one trap every one of these modules has to avoid.
import { dayKey } from './contract';

/**
 * The four dimensions of the 2016 Ratio Fundamentalis (concept §2.1, §4.1).
 *
 * `key` is the file format (lib/formation_org.js writes it as an Org tag), the
 * rest is copy and can be reworded freely without losing anybody's state.
 *
 * Their source insists the four form one journey rather than four subjects to be
 * passed separately, which is why nothing in this module ever scores them
 * individually or rolls them up into a total (§3.6).
 */
export const DIMENSIONS = [
  {
    key: 'human',
    label: 'Human',
    gloss:
      'Character, maturity, how I actually am with people. PDV §43 makes this the ground the other three stand on — a deficit here is not one weak subject, it is a crack under all of them.',
  },
  {
    key: 'spiritual',
    label: 'Spiritual',
    // Deliberately the thinnest row on the page (§6): one line and a date. What
    // happens here is not the business of a web app, and there is no streak.
    gloss:
      'Prayer, sacraments, direction. One line only, and no counter — a streak would turn a relationship into a metric (§3.2).',
  },
  {
    key: 'intellectual',
    label: 'Intellectual',
    gloss: 'Study, theology, what I actually read rather than meant to.',
  },
  {
    key: 'pastoral',
    label: 'Pastoral',
    gloss: 'The craft itself: preaching, Seelsorge, teaching, presence at the hard moments.',
  },
];

export const DIMENSION_KEYS = DIMENSIONS.map((dimension) => dimension.key);

/**
 * The five logged behaviours of §4.3's right-hand column.
 *
 * Taken from what the role-modelling literature actually distinguishes: Wright
 * et al. (1998) found excellent role models separated by time given, the
 * relationship stressed and teaching - not rank or output. Each is a thing a
 * person did on a given day, which is loggable; "being exemplary" is not.
 */
export const PRACTICES = [
  { key: 'time', label: 'Gave someone unhurried time' },
  { key: 'relation', label: 'Put the relationship before the task' },
  {
    key: 'teaching',
    label: 'Said out loud what I was doing and why',
    // Cruess et al. (2008) in one line: role modelling runs on unconscious
    // components and transmits poor practice as efficiently as good. Articulating
    // what you are doing is what turns it into teaching rather than chance.
    why: 'What turns unconscious modelling into teaching (Cruess et al. 2008).',
  },
  {
    key: 'error',
    label: 'Admitted a mistake where it could be heard',
    why: 'The first thing someone chasing exemplarity stops doing (§2.5).',
  },
  {
    key: 'help',
    label: 'Asked for help where I was out of my depth',
    why: 'Listed as something a role model does, not something a role model needs (§2.5).',
  },
];

export const PRACTICE_KEYS = PRACTICES.map((practice) => practice.key);

// Whether the commitment made at the *previous* formation conversation actually
// happened (§4.4). The only fixed-answer question about the user in the whole
// tool - and it asks about an event, not a quality.
export const COMMITMENT_STATES = ['yes', 'partly', 'no'];

export const COMMITMENT_LABELS = {
  yes: 'kept',
  partly: 'partly',
  no: 'not kept',
};

// One persistent file, not a daily note (concept §5.2) - overridable through the
// `formationPath` setting.
export const DEFAULT_FORMATION_PATH = '/roam/formation.org';

// How long before a dimension counts as cold and the formation conversation is
// due (§5.4). Guessed rather than derived - concept §7.2 says so out loud.
export const DEFAULT_FORMATION_INTERVAL_DAYS = 28;

const DAY_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const isDayKey = (value) => typeof value === 'string' && DAY_KEY_REGEX.test(value);

const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;

/** A day key back to a local midnight Date; null for anything that is not one. */
export const dateForDayKey = (key) => {
  if (!isDayKey(key)) return null;
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Whole days from `fromKey` to `toKey`, or null if either is not a day key. */
export const daysBetween = (fromKey, toKey) => {
  const from = dateForDayKey(fromKey);
  const to = dateForDayKey(toKey);
  if (!from || !to) return null;

  // Rounded, not floored: a DST boundary inside the span shifts the difference
  // by an hour, and that must not turn 28 days into 27.
  return Math.round((to.getTime() - from.getTime()) / MILLIS_PER_DAY);
};

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

// Every text field here is stored as exactly one line of an Org file
// (lib/formation_org.js), so a pasted paragraph is folded into one line on the
// way in rather than breaking the file's structure on the way out.
const oneLine = (value) => trimmed(value).replace(/\s+/g, ' ');

const byDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

const positiveInterval = (intervalDays) => {
  const interval = Number(intervalDays);
  return Number.isFinite(interval) && interval > 0 ? interval : DEFAULT_FORMATION_INTERVAL_DAYS;
};

/**
 * The four dimension rows, always all four, in the order of DIMENSIONS -
 * whatever was read out of the file (or nothing at all).
 *
 * An unknown key is dropped and a missing one comes back with an empty note: the
 * file is hand-editable, and one mangled line must not take the page down.
 */
export const normalizeDimensions = (dimensions) => {
  const byKey = {};
  (Array.isArray(dimensions) ? dimensions : []).forEach((entry) => {
    if (entry && entry.key) byKey[entry.key] = entry;
  });

  return DIMENSIONS.map((dimension) => ({
    key: dimension.key,
    label: dimension.label,
    gloss: dimension.gloss,
    note: oneLine((byKey[dimension.key] || {}).note),
  }));
};

/**
 * Encounter records, oldest first (concept §4.2).
 *
 * Only `said` is structurally required - an entry with no words in it is not a
 * verbatim. `felt` and `differently` may legitimately be missing here even
 * though the page's gate requires them, exactly as lib/team.js tolerates a
 * review entry with no verification line: the file is the user's, and a record
 * hand-written in Emacs must not be discarded for being incomplete.
 *
 * `who` is initials or a pseudonym (§5.3). Nothing enforces that - it cannot be
 * enforced - but nothing in the design ever asks for more.
 */
export const normalizeEncounters = (encounters) =>
  (Array.isArray(encounters) ? encounters : [])
    .filter((entry) => entry && isDayKey(entry.date) && !!oneLine(entry.said))
    .map((entry) => ({
      date: entry.date,
      who: oneLine(entry.who),
      said: oneLine(entry.said),
      felt: oneLine(entry.felt),
      differently: oneLine(entry.differently),
      avoided: oneLine(entry.avoided),
      dimension: DIMENSION_KEYS.includes(entry.dimension) ? entry.dimension : null,
    }))
    .sort(byDate);

/**
 * What other people actually said or did, oldest first (§4.3, left column).
 *
 * Stored verbatim on purpose: Kluger & DeNisi (1996) put roughly a third of
 * feedback interventions on the wrong side of zero, with the moderator being
 * whether attention lands on the task or on the person. A quoted remark stays on
 * the task side in a way a remembered impression does not.
 */
export const normalizeObserved = (observed) =>
  (Array.isArray(observed) ? observed : [])
    .filter((entry) => entry && isDayKey(entry.date) && !!oneLine(entry.what))
    .map((entry) => ({
      date: entry.date,
      who: oneLine(entry.who),
      what: oneLine(entry.what),
    }))
    .sort(byDate);

/**
 * Logged behaviours, oldest first (§4.3, right column) - one entry per day,
 * carrying the set of behaviours logged that day.
 *
 * Merged per day rather than appended per tap: a day is a fact, and two taps of
 * the same behaviour on one day are the same fact twice. That also means no
 * amount of tapping can inflate anything, which matters because §3.1 forbids the
 * count these would otherwise feed.
 */
export const normalizePractised = (practised) => {
  const byDay = {};

  (Array.isArray(practised) ? practised : []).forEach((entry) => {
    if (!entry || !isDayKey(entry.date)) return;

    const keys = (Array.isArray(entry.keys) ? entry.keys : []).filter((key) =>
      PRACTICE_KEYS.includes(key)
    );
    if (!keys.length) return;

    const merged = new Set([...(byDay[entry.date] || []), ...keys]);
    byDay[entry.date] = [...merged];
  });

  return Object.keys(byDay)
    .map((date) => ({
      date,
      // Held in PRACTICES order rather than tap order, so the file does not
      // churn when the same day is written twice.
      keys: PRACTICE_KEYS.filter((key) => byDay[date].includes(key)),
    }))
    .sort(byDate);
};

/**
 * Formation conversations, oldest first (§4.4).
 *
 * `kept` refers to the commitment made at the *previous* conversation, and null
 * is a real value - the first conversation has no previous commitment to report
 * on, and "never answered" must stay distinguishable from "answered no".
 */
export const normalizeConversations = (conversations) =>
  (Array.isArray(conversations) ? conversations : [])
    .filter((entry) => entry && isDayKey(entry.date) && !!oneLine(entry.brought))
    .map((entry) => ({
      date: entry.date,
      brought: oneLine(entry.brought),
      named: oneLine(entry.named),
      committed: oneLine(entry.committed),
      kept: COMMITMENT_STATES.includes(entry.kept) ? entry.kept : null,
    }))
    .sort(byDate);

/** Whatever was read out of the file into the one shape this module works on. */
export const normalizeFormationState = (state) => ({
  dimensions: normalizeDimensions(state && state.dimensions),
  encounters: normalizeEncounters(state && state.encounters),
  observed: normalizeObserved(state && state.observed),
  practised: normalizePractised(state && state.practised),
  conversations: normalizeConversations(state && state.conversations),
});

/** A state with nothing in it yet: four empty dimension rows and four empty logs. */
export const emptyFormationState = () => normalizeFormationState(null);

/** One free-text line per dimension (§4.1): what is being worked on there. */
export const setDimensionNote = (state, key, note) => {
  const normalized = normalizeFormationState(state);

  return {
    ...normalized,
    dimensions: normalized.dimensions.map((entry) =>
      entry.key === key ? { ...entry, note: oneLine(note) } : entry
    ),
  };
};

/**
 * Append one encounter (§4.2). Needs at least the words that were said.
 *
 * The page will not offer the button without `felt` and `differently` too - the
 * gate is there because an entry with no self-implication is a diary entry, and
 * self-implication is the active ingredient of the method (§2.2).
 */
export const addEncounter = (
  state,
  { who, said, felt, differently, avoided, dimension, today }
) => {
  const normalized = normalizeFormationState(state);
  const words = oneLine(said);
  if (!words) return normalized;

  return {
    ...normalized,
    encounters: normalizeEncounters([
      ...normalized.encounters,
      { date: dayKey(today), who, said: words, felt, differently, avoided, dimension },
    ]),
  };
};

/** Append something someone actually said or did (§4.3). Needs the remark itself. */
export const addObservation = (state, { who, what, today }) => {
  const normalized = normalizeFormationState(state);
  const remark = oneLine(what);
  if (!remark) return normalized;

  return {
    ...normalized,
    observed: normalizeObserved([
      ...normalized.observed,
      { date: dayKey(today), who, what: remark },
    ]),
  };
};

/**
 * Log a behaviour for today (§4.3). Logging one twice in a day is a no-op rather
 * than a second entry - see normalizePractised.
 */
export const addPractice = (state, { key, today }) => {
  const normalized = normalizeFormationState(state);
  if (!PRACTICE_KEYS.includes(key)) return normalized;

  return {
    ...normalized,
    practised: normalizePractised([...normalized.practised, { date: dayKey(today), keys: [key] }]),
  };
};

/** Append one formation conversation (§4.4). Needs at least what was brought to it. */
export const addConversation = (state, { brought, named, committed, kept, today }) => {
  const normalized = normalizeFormationState(state);
  const topic = oneLine(brought);
  if (!topic) return normalized;

  return {
    ...normalized,
    conversations: normalizeConversations([
      ...normalized.conversations,
      { date: dayKey(today), brought: topic, named, committed, kept },
    ]),
  };
};

/**
 * Per dimension: when it was last touched, how many encounters in the window are
 * tagged to it, and whether it has gone cold (concept §4.1).
 *
 * Recency and balance, never quality. The intended reading is negative space -
 * "nothing under human in seven weeks" is a checkable fact about a calendar,
 * where "3 out of 5 on the human dimension" is not a fact at all (§2.3).
 *
 * A dimension with a note but no encounters is *not* cold: writing down what you
 * are working on there is touching it. `lastTouched` therefore takes the later
 * of the two, and is null only when neither exists.
 */
export const dimensionStatus = (state, today, intervalDays = DEFAULT_FORMATION_INTERVAL_DAYS) => {
  const normalized = normalizeFormationState(state);
  const days = positiveInterval(intervalDays);
  const todayKey = dayKey(today);

  return normalized.dimensions.map((dimension) => {
    const tagged = normalized.encounters.filter((entry) => entry.dimension === dimension.key);
    const latest = tagged.length ? tagged[tagged.length - 1].date : null;

    const daysSince = latest === null ? null : daysBetween(latest, todayKey);

    return {
      ...dimension,
      encounters: tagged.length,
      lastTouched: latest,
      daysSince,
      // Never touched counts as cold: that is the state a new file starts in, and
      // a dimension nobody has ever written under is exactly what §4.1 exists to
      // surface. A note alone keeps it warm - see the doc comment above.
      cold: !dimension.note && (daysSince === null || daysSince >= days),
      intervalDays: days,
    };
  });
};

/**
 * Per behaviour: the date it was last logged, or null (§4.3, right column).
 *
 * Recency only. §3.1 rules out the obvious "3 of 5 this week", because a count of
 * one's own good behaviour is the licensing machine §2.5 warns about, and because
 * the count would be of self-report either way.
 */
export const practiceRecency = (practised) => {
  const entries = normalizePractised(practised);

  return PRACTICES.map((practice) => {
    const dated = entries.filter((entry) => entry.keys.includes(practice.key));

    return {
      ...practice,
      lastDate: dated.length ? dated[dated.length - 1].date : null,
    };
  });
};

/**
 * The behaviours already logged for `today` (§4.3), so the page can show those
 * as done rather than offering a tap that would be a no-op.
 */
export const practisedOn = (practised, today) => {
  const key = dayKey(today);
  const entry = normalizePractised(practised).find((item) => item.date === key);

  return entry ? entry.keys : [];
};

/**
 * The kept-commitment tally (§4.4): a count of promises, not a score of a person.
 *
 * `unanswered` is shown rather than folded into `no` - the first conversation has
 * no previous commitment to report on, and an entry where the question was never
 * answered is not evidence that the commitment was broken.
 */
export const commitmentTally = (conversations) => {
  const entries = normalizeConversations(conversations);
  const counted = (value) => entries.filter((entry) => entry.kept === value).length;

  return {
    total: entries.length,
    kept: counted('yes'),
    partly: counted('partly'),
    notKept: counted('no'),
    unanswered: entries.filter((entry) => entry.kept === null).length,
  };
};

/**
 * Whether it is time for the next formation conversation (§4.4).
 *
 * With none logged yet it is due - that is the state a new file starts in, and a
 * reminder is the whole point of the section. `daysSince` is null in that case
 * rather than a made-up zero.
 */
export const conversationDue = (
  conversations,
  today,
  intervalDays = DEFAULT_FORMATION_INTERVAL_DAYS
) => {
  const entries = normalizeConversations(conversations);
  const latest = entries.length ? entries[entries.length - 1] : null;
  const days = positiveInterval(intervalDays);

  if (!latest) return { due: true, daysSince: null, lastDate: null, intervalDays: days };

  const daysSince = daysBetween(latest.date, dayKey(today));

  return {
    due: daysSince === null || daysSince >= days,
    daysSince,
    lastDate: latest.date,
    intervalDays: days,
  };
};

/** The open commitment the next conversation has to report back on (§4.4), or null. */
export const openCommitment = (conversations) => {
  const entries = normalizeConversations(conversations);

  for (let index = entries.length - 1; index >= 0; index -= 1) {
    if (entries[index].committed) return entries[index];
  }

  return null;
};

/**
 * The cards of §4.5: what keeps this page honest.
 *
 * Static on purpose - §3.7 rejects generating any of the user's reflection for
 * them, and these are not a form either. The `strength` line is content rather
 * than decoration: a reader should see which of these rests on a meta-analysis,
 * which on a contested classic, and which is not an empirical claim at all.
 *
 * They are here because the failure mode of this page is not disuse. It is
 * enthusiastic use.
 */
export const HONESTY_CARDS = [
  {
    text: 'Practice will not make you good at this the way it makes you good at chess.',
    why:
      'Deliberate practice explained 26% of performance variance in games and 21% in music — but 4% in education and under 1% in the professions. Pastoral work is the least structured kind of domain there is, which is where the effect all but vanishes.',
    strength: 'strong (Macnamara, Hambrick & Oswald 2014, meta-analysis)',
  },
  {
    text: 'You are the worst available judge of how you are doing — and most wrong when most sure.',
    why:
      'Across 20 comparisons of physician self-assessment against observed competence, 13 showed little, no, or an inverse relationship. Accuracy was worst among the least skilled and the most confident.',
    strength: 'strong (Davis et al. 2006, JAMA, systematic review)',
  },
  {
    text: 'Nobody becomes a Vorbild by deciding to. It is conferred by the people watching.',
    why:
      'Role modelling works largely unconsciously on both sides — and transmits poor practice exactly as efficiently as good practice. Every study in this literature identifies role models by asking the learners; none asks the candidate.',
    strength: 'strong (Cruess, Cruess & Steinert 2008, BMJ)',
  },
  {
    text: 'Feedback about the person hurts. Feedback about the act helps.',
    why:
      'Feedback raises performance on average and lowers it in roughly a third of cases; what moves it is whether attention lands on the task or on the self. "You are a natural" is not safer than "you did that badly" — it is the same kind of remark.',
    strength: 'strong, double-edged (Kluger & DeNisi 1996, 607 effect sizes)',
  },
  {
    text:
      '"Beware of practising your righteousness before others in order to be seen by them." (Mt 6:1)',
    why:
      'The sharpest critique of this page is in the tradition it serves. It is not answered by argument but by design: nothing here scores you, totals you, or ranks you against anyone. If this page ever grows a Vorbild-Index, it has lost.',
    strength: 'not an empirical claim, and the load-bearing one anyway',
  },
];
